// grid call <to-entity> <path> — backlog 192 Phase 5 / decision 027a.
//
// Developer debug helper for cross-app calls. Acquires an SA token via the
// existing /api/v2/entities/:id/caller-grants endpoints (decision 027a),
// then fires an HTTP request to the target entity's serving URL with the
// X-CloudGrid-Caller-Token header attached. Friendly §23 wording for the
// four documented failure classes (revoked, pending, target-missing,
// cross-org).
//
// Token acquisition is debug-only: it does NOT cache tokens. Every
// invocation re-mints. Production entities receive their token via the
// K8s Secret + env var mounted by the deployer (decision 027a §"Storage").

import { Command } from 'commander';
import { parseCallerAddress } from '../../../shared/src/caller-address.js';
import { readLink } from '../services/link.js';
import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { V2ApiError } from '../api/v2-errors.js';
import { CloudGridError, isVerbose } from '../utils.js';
import { handleV2Error } from '../handle-error.js';
import type { V2RuntimeDetailResponse } from './status.js';

// ── Address parsing ───────────────────────────────────────────────────────

export interface CallTarget {
  orgSlug: string;
  entitySlug: string;
  raw: string;
}

/**
 * Parse a target address. Accepts only `org:<slug>/<entity>` — `space:`,
 * wildcards, and bare names are rejected. Reuses `parseCallerAddress` for
 * the syntax check; layers on the `scope: 'org'` + non-wildcard constraints
 * the call verb requires (you can't call a wildcard).
 */
export function parseCallTarget(addr: string): CallTarget {
  const parsed = parseCallerAddress(addr);
  if (!parsed || parsed.scope !== 'org' || parsed.isWildcard) {
    throw new CloudGridError(
      `${JSON.stringify(addr)} is not a valid call target. Use org:<org-slug>/<entity-slug> (e.g. org:atomic/billing-service).`,
    );
  }
  return { orgSlug: parsed.ownerSlug, entitySlug: parsed.entitySlug, raw: addr };
}

// ── Caller resolution ─────────────────────────────────────────────────────

/**
 * Resolve the caller's address. Priority:
 *   1. --from flag → parsed as a CallTarget
 *   2. .cloudgrid/link.json → composed from link.org_slug + link.entity_slug
 *   3. Neither → CloudGridError with friendly copy
 *
 * Pre-0.8.7 link.json files lack `entity_slug`; those throw a re-link hint
 * rather than silently mis-composing the address.
 */
export function resolveCallerAddress(fromFlag: string | undefined): CallTarget {
  if (fromFlag && fromFlag.trim().length > 0) {
    return parseCallTarget(fromFlag.trim());
  }
  const link = readLink();
  if (!link) {
    throw new CloudGridError(
      "No caller entity. Run grid call inside a linked directory, or pass --from <org:slug/entity>.",
    );
  }
  if (!link.entity_slug) {
    throw new CloudGridError(
      "The link in this directory is from an older CLI and doesn't record the entity slug. Re-link with grid init, or pass --from <org:slug/entity>.",
    );
  }
  return parseCallTarget(`org:${link.org_slug}/${link.entity_slug}`);
}

// ── Entity lookup ─────────────────────────────────────────────────────────

/**
 * Fetch the canonical runtime detail for a call address. Translates the
 * 404 path into a §23-voice "no entity at <address>" error; other errors
 * propagate. Phase 1 same-org only — when the address's org-slug doesn't
 * match the client's active org, the server returns 404 (UUID-leak guard);
 * the user sees the same friendly copy.
 */
export async function lookupCallEntity(
  client: IV2Client,
  target: CallTarget,
): Promise<V2RuntimeDetailResponse> {
  try {
    return await client.get<V2RuntimeDetailResponse>(
      `/api/v2/runtimes/${encodeURIComponent(target.entitySlug)}`,
    );
  } catch (err) {
    if (err instanceof V2ApiError && err.status === 404) {
      throw new CloudGridError(
        `No entity at ${target.raw}. Check the address or run grid list to see entities you can reach.`,
      );
    }
    throw err;
  }
}

// ── Token acquisition ─────────────────────────────────────────────────────

export interface CallerEntityLite {
  entity_id: string;
  name: string;
}

export type CallerTokenResult =
  | { kind: 'minted'; token: string; grant: Record<string, unknown> }
  | { kind: 'pending'; grant: Record<string, unknown> };

/**
 * Acquire an SA token for caller A → target B using only the existing
 * caller-grants endpoints (decision 027a §"Grant lifecycle"):
 *
 *   1. POST /entities/<A>/caller-grants {to_entity_id: <B>}
 *      - Phase 2 auto-policy → returns a token immediately. Use it.
 *      - Phase 1 manual + new request → grant.status='requested', no token.
 *        Return 'pending' so the caller renders the friendly copy.
 *      - Idempotent path (existing approved grant) → grant.status='approved',
 *        no token. Fall through to step 2.
 *   2. POST /entities/<B>/caller-grants/<grant_id>/approve — idempotent
 *      re-mint on already-approved grants. Requires admin in B's org.
 */
export async function acquireCallerToken(
  client: IV2Client,
  caller: CallerEntityLite,
  target: CallerEntityLite,
): Promise<CallerTokenResult> {
  let created: { grant: Record<string, unknown>; token: string | null };
  try {
    created = await client.post<{ grant: Record<string, unknown>; token: string | null }>(
      `/api/v2/entities/${encodeURIComponent(caller.entity_id)}/caller-grants`,
      { to_entity_id: target.entity_id },
    );
  } catch (err) {
    if (err instanceof V2ApiError) {
      if (err.code === 'CROSS_ORG_NOT_SUPPORTED') {
        throw new CloudGridError(
          "Cross-grid calls aren't allowed yet. The caller and target must be in the same grid.",
        );
      }
      if (err.code === 'CALLER_TARGET_NOT_FOUND') {
        throw new CloudGridError(
          `No entity at ${target.name}. Check the address or run grid list to see entities you can reach.`,
        );
      }
    }
    throw err;
  }

  const grant = created.grant ?? {};
  const status = typeof grant.status === 'string' ? grant.status : 'unknown';

  if (created.token) {
    return { kind: 'minted', token: created.token, grant };
  }
  if (status === 'requested') {
    return { kind: 'pending', grant };
  }
  if (status === 'approved') {
    const grantId = typeof grant.grant_id === 'string' && grant.grant_id.length > 0
      ? grant.grant_id
      : null;
    if (grantId === null) {
      // The server returned an approved grant without an identifier we
      // can target the /approve endpoint with. Treat as a contract
      // violation rather than silently composing `.../caller-grants//approve`
      // and surfacing the resulting 404.
      throw new CloudGridError(
        "Unexpected server response: approved grant is missing grant_id. Re-run, and if it persists report the issue.",
      );
    }
    try {
      const approved = await client.post<{ grant: Record<string, unknown>; token: string }>(
        `/api/v2/entities/${encodeURIComponent(target.entity_id)}/caller-grants/${encodeURIComponent(grantId)}/approve`,
        {},
      );
      return { kind: 'minted', token: approved.token, grant: approved.grant };
    } catch (err) {
      if (err instanceof V2ApiError && err.status === 403) {
        throw new CloudGridError(
          "Need admin role in the target's grid to mint a token for testing. Have an admin approve the grant in the Console at https://console.cloudgrid.io.",
        );
      }
      throw err;
    }
  }
  // Terminal states (revoked / rejected) — surface as a generic error.
  throw new CloudGridError(
    `Grant is in terminal state '${status}'. Re-request the grant from the Console at https://console.cloudgrid.io.`,
  );
}

// ── Outbound HTTP execution ───────────────────────────────────────────────

export interface CallRequestInput {
  /** Target entity's serving URL (no trailing slash, no path). */
  url: string;
  /** Path component the user supplied (must start with /). */
  path: string;
  method: string;
  body: string | undefined;
  /** Raw -H values: 'Key: Value'. */
  headers: string[];
  /** Minted SA token. */
  token: string;
}

export interface CallResponse {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  body: string;
}

export type FetchImpl = (url: string, init: RequestInit) => Promise<Response>;

/**
 * Compose the full outbound URL from an entity's serving URL and a
 * user-supplied path. Strips a trailing slash from the URL and ensures the
 * path starts with one — every caller in this verb uses the same shape.
 */
function composeOutboundUrl(servingUrl: string, path: string): string {
  return `${servingUrl.replace(/\/+$/, '')}${path.startsWith('/') ? path : '/' + path}`;
}

/**
 * Send the outbound HTTP request. Translates the four sidecar 403 error
 * codes into §23-voice CloudGridError; any other non-sidecar response
 * (including 4xx/5xx from the user's own code) is returned unchanged so
 * the developer can inspect their app's behavior.
 */
export async function sendCallerRequest(
  input: CallRequestInput,
  fetchImpl: FetchImpl = (url, init) => fetch(url, init),
): Promise<CallResponse> {
  const fullUrl = composeOutboundUrl(input.url, input.path);

  const headers: Record<string, string> = {};
  for (const h of input.headers) {
    const idx = h.indexOf(':');
    if (idx === -1) {
      throw new CloudGridError(
        `Header ${JSON.stringify(h)} is malformed. Use -H 'Key: Value'.`,
      );
    }
    const key = h.slice(0, idx).trim();
    const value = h.slice(idx + 1).trim();
    headers[key] = value;
  }
  if (input.body !== undefined) {
    try {
      JSON.parse(input.body);
      headers['Content-Type'] ??= 'application/json';
    } catch {
      headers['Content-Type'] ??= 'text/plain';
    }
  }
  headers['X-CloudGrid-Caller-Token'] = input.token;

  const init: RequestInit = {
    method: input.method,
    headers,
    body: input.body,
  };

  const res = await fetchImpl(fullUrl, init);
  const text = await res.text();

  // Sidecar error codes — translate to §23 copy.
  if (res.status === 403 && (res.headers.get('content-type') ?? '').includes('application/json')) {
    let parsed: { error?: { code?: string } } | null = null;
    try { parsed = JSON.parse(text); } catch { /* fall through */ }
    const code = parsed?.error?.code;
    if (code === 'GRANT_REVOKED') {
      throw new CloudGridError(
        "Grant is revoked. The target won't let you call it. To request access again, edit your cloudgrid.yaml's callers: block and re-plug.",
      );
    }
    if (code === 'GRANT_PENDING') {
      throw new CloudGridError(
        "Grant is pending approval from the target's owner. They need to approve it in the Console first.",
      );
    }
    if (code === 'CALLER_TOKEN_INVALID' || code === 'CALLER_TOKEN_AUD_MISMATCH') {
      throw new CloudGridError(
        "The caller token didn't verify. Run grid call --verbose to see the decoded claims, or re-plug to rotate.",
      );
    }
  }

  const respHeaders: Record<string, string> = {};
  res.headers.forEach((v, k) => { respHeaders[k] = v; });
  return { status: res.status, statusText: res.statusText, headers: respHeaders, body: text };
}

// ── Token claims decoding (for --verbose / --json) ────────────────────────

interface DecodedClaims {
  iss?: string;
  sub?: string;
  aud?: string;
  iat?: number;
  nbf?: number;
  exp?: number;
  jti?: string;
}

function decodeJwtClaims(token: string): DecodedClaims | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const json = Buffer.from(parts[1], 'base64url').toString('utf-8');
    const parsed = JSON.parse(json);
    if (parsed && typeof parsed === 'object') return parsed as DecodedClaims;
    return null;
  } catch {
    return null;
  }
}

// ── Rendering ─────────────────────────────────────────────────────────────

function renderDefault(out: NodeJS.WriteStream, resp: CallResponse): void {
  out.write(`${resp.status} ${resp.statusText}\n`);
  out.write(resp.body);
  if (!resp.body.endsWith('\n')) out.write('\n');
}

function renderJson(
  out: NodeJS.WriteStream,
  resp: CallResponse,
  grant: Record<string, unknown>,
  claims: DecodedClaims | null,
): void {
  out.write(JSON.stringify({
    status: resp.status,
    headers: resp.headers,
    body: resp.body,
    grant,
    ...(claims ? { claims } : {}),
  }) + '\n');
}

function renderVerbose(
  out: NodeJS.WriteStream,
  resp: CallResponse,
  reqUrl: string,
  reqHeaders: Record<string, string>,
  grant: Record<string, unknown>,
  claims: DecodedClaims | null,
): void {
  out.write(`Grant: ${JSON.stringify(grant)}\n`);
  if (claims) {
    out.write(`Claims: ${JSON.stringify(claims)}\n`);
  }
  out.write(`\nRequest: ${reqUrl}\n`);
  for (const [k, v] of Object.entries(reqHeaders)) {
    out.write(`  ${k}: ${v}\n`);
  }
  out.write(`\nResponse: ${resp.status} ${resp.statusText}\n`);
  for (const [k, v] of Object.entries(resp.headers)) {
    out.write(`  ${k}: ${v}\n`);
  }
  out.write(`\n${resp.body}`);
  if (!resp.body.endsWith('\n')) out.write('\n');
}

// ── Command implementation ────────────────────────────────────────────────

export interface CallOpts {
  from?: string;
  method?: string;
  data?: string;
  header?: string[];
  verbose?: boolean;
  json?: boolean;
}

export async function runCall(
  toAddr: string,
  path: string,
  opts: CallOpts,
  client?: IV2Client,
  fetchImpl?: FetchImpl,
): Promise<void> {
  const apiClient = client ?? (await getV2Client());

  const target = parseCallTarget(toAddr);
  const callerAddr = resolveCallerAddress(opts.from);

  const callerDetail = await lookupCallEntity(apiClient, callerAddr);
  const targetDetail = await lookupCallEntity(apiClient, target);

  const tokenResult = await acquireCallerToken(
    apiClient,
    { entity_id: callerDetail.entity_id, name: callerDetail.name },
    { entity_id: targetDetail.entity_id, name: targetDetail.name },
  );
  if (tokenResult.kind === 'pending') {
    throw new CloudGridError(
      "Grant is pending approval from the target's owner. They need to approve it in the Console first. See https://console.cloudgrid.io.",
    );
  }

  const headers = opts.header ?? [];
  const method = (opts.method ?? 'GET').toUpperCase();
  const reqUrl = composeOutboundUrl(targetDetail.url, path);

  // Mirror sendCallerRequest's header derivation for the verbose render.
  // The actual send rebuilds this internally; we replay it here so the
  // verbose output reflects what was actually attached.
  const previewHeaders: Record<string, string> = {};
  for (const h of headers) {
    const idx = h.indexOf(':');
    if (idx > 0) previewHeaders[h.slice(0, idx).trim()] = h.slice(idx + 1).trim();
  }
  previewHeaders['X-CloudGrid-Caller-Token'] = tokenResult.token;
  if (opts.data !== undefined) {
    try { JSON.parse(opts.data); previewHeaders['Content-Type'] ??= 'application/json'; }
    catch { previewHeaders['Content-Type'] ??= 'text/plain'; }
  }

  const resp = await sendCallerRequest({
    url: targetDetail.url,
    path,
    method,
    body: opts.data,
    headers,
    token: tokenResult.token,
  }, fetchImpl);

  // Either the per-command `--verbose` flag or the root `-v/--verbose`
  // flag should trigger verbose output. The root flag is set by index.ts's
  // preAction hook before the command's action runs, so isVerbose() is
  // authoritative there.
  const verbose = !!opts.verbose || isVerbose();
  const claims = verbose || opts.json ? decodeJwtClaims(tokenResult.token) : null;

  if (opts.json) {
    renderJson(process.stdout, resp, tokenResult.grant, claims);
  } else if (verbose) {
    renderVerbose(process.stdout, resp, reqUrl, previewHeaders, tokenResult.grant, claims);
  } else {
    renderDefault(process.stdout, resp);
  }
}

// ── Commander command export ──────────────────────────────────────────────

export const callCommand = new Command('call')
  .description('Send a test request from one entity to another')
  .argument('<to-entity>', 'Target address (e.g. org:atomic/billing-service)')
  .argument('<path>', 'HTTP path on the target (e.g. /api/health)')
  .option('--from <addr>', 'Caller entity address (overrides .cloudgrid/link.json)')
  .option('-X, --method <method>', 'HTTP method', 'GET')
  .option('-d, --data <body>', 'Request body (sent as-is)')
  .option('-H, --header <header...>', 'Additional request headers (Key: Value)')
  .option('--verbose', 'Show grant state, decoded claims, and full headers')
  .option('--json', 'Machine-readable JSON output')
  .action(async (toEntity: string, path: string, opts: CallOpts) => {
    try {
      await runCall(toEntity, path, opts);
    } catch (err) {
      if (err instanceof CloudGridError) {
        process.stderr.write(`Error: ${err.message}\n`);
        process.exit(1);
      }
      await handleV2Error(err, { json: !!opts.json });
    }
  });
